import ApiAcces from "@/shared/services/ApiAcces";
import type IBrandingService from "../contracts/IBrandingService";
import type {
  BrandingColors,
  PublicBranding,
  TenantBranding,
} from "../models/Branding";

export default class BrandingService
  extends ApiAcces
  implements IBrandingService
{
  async get(): Promise<TenantBranding> {
    return (await this.api.get("/api/branding")).data;
  }

  async getInherited(): Promise<PublicBranding> {
    return (await this.api.get("/api/branding/me")).data;
  }

  async updateAppearance(
    appName: string | null,
    colors: BrandingColors,
  ): Promise<TenantBranding> {
    return (await this.api.put("/api/branding/appearance", { appName, colors }))
      .data;
  }

  async uploadLogo(file: File): Promise<TenantBranding> {
    return this.uploadAsset("/api/branding/logo", file);
  }

  async uploadFavicon(file: File): Promise<TenantBranding> {
    return this.uploadAsset("/api/branding/favicon", file);
  }

  private async uploadAsset(url: string, file: File): Promise<TenantBranding> {
    const form = new FormData();
    form.append("file", file);
    return (
      await this.api.post(url, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  }

  async setDomain(customDomain: string): Promise<TenantBranding> {
    return (await this.api.put("/api/branding/domain", { customDomain })).data;
  }

  async verifyDomain(): Promise<TenantBranding> {
    return (await this.api.post("/api/branding/domain/verify")).data;
  }

  async resolvePublic(host?: string): Promise<PublicBranding | null> {
    const res = await this.api.get("/api/branding/public/resolve", {
      params: host ? { host } : undefined,
    });
    return res.status === 204 ? null : (res.data ?? null);
  }

  resolveAssetUrl(path: string | null | undefined): string | undefined {
    if (!path) return undefined;
    if (/^https?:\/\//i.test(path)) return path;
    const base = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");
    return `${base}${path}`;
  }
}
